// Merging duplicates (AB3, admins only) in a dialog: a company into another (target by the company search, a preview of
// what moves), a person into another (of the company, or found by the search), an address into another of the company.
// The duplicate stays as a reference to the survivor; the apps that keep our ids hear "alt → neu".
import { ApiError, type MergePreview } from "../api";
import { loadCompany, render, S } from "../state";
import { esc, toast } from "../util";
import { ADDRESS_KIND, addressLine } from "./firma";

/** A record to merge or to merge into; `version` null: found by the search, read when confirmed. */
interface Choice {
  id: string;
  version: number | null;
  label: string;
  hint: string;
  companyId?: string | null;
}

type Kind = "company" | "contact" | "address";

const state = {
  kind: "company" as Kind,
  companyId: "",
  source: null as Choice | null,
  /** Offered without searching: the company's other persons or addresses, the other companies of a duplicate group. */
  offered: [] as Choice[],
  found: null as Choice[] | null,
  q: "",
  target: null as Choice | null,
  preview: null as MergePreview | null,
  busy: false,
};
let timer: ReturnType<typeof setTimeout> | undefined;

const NOUN: Record<Kind, { one: string; search: string | null }> = {
  company: { one: "Firma", search: "Firma suchen: Name, Domain oder Ort" },
  contact: { one: "Person", search: "Person einer anderen Firma suchen: Name oder Mail" },
  address: { one: "Adresse", search: null },
};

function dialog(): HTMLDialogElement {
  let d = document.querySelector<HTMLDialogElement>("dialog#merge");
  if (!d) {
    d = document.createElement("dialog");
    d.id = "merge";
    d.className = "orig merge";
    d.addEventListener("click", (e) => void onClick(e, d!));
    d.addEventListener("input", (e) => onInput(e));
    d.addEventListener("close", () => clearTimeout(timer));
    document.body.append(d);
  }
  return d;
}

function open(kind: Kind, companyId: string, source: Choice, offered: Choice[]): void {
  Object.assign(state, { kind, companyId, source, offered, found: null, q: "", target: null, preview: null, busy: false });
  const d = dialog();
  paint(d);
  d.showModal();
  d.querySelector<HTMLInputElement>("input[name=q]")?.focus();
}

const moveLine = (n: number, one: string, many: string) => `<li><span>${n === 1 ? one : many}</span><span class="r">${n}</span></li>`;

function paint(d: HTMLDialogElement): void {
  const { kind, source, target, preview } = state;
  const noun = NOUN[kind];
  const head = `<div class="orig-h"><div class="orig-meta"><b>${esc(source!.label)} zusammenführen</b><span>${esc(source!.hint)}</span></div>
    <button class="xbtn" data-merge-close aria-label="Schließen">×</button></div>`;
  if (!target) {
    const list = [...state.offered, ...(state.found ?? [])].filter((c, i, all) => c.id !== source!.id && all.findIndex((x) => x.id === c.id) === i);
    d.innerHTML = `${head}<div class="merge-b">
      <p>In welche ${noun.one} geht „${esc(source!.label)}“ auf? Die gewählte bleibt und behält ihre Angaben; leere Felder werden ergänzt.</p>
      ${noun.search ? `<input name="q" type="search" placeholder="${noun.search}" value="${esc(state.q)}" autocomplete="off">` : ""}
      ${
        list.length
          ? `<ul class="merge-list">${list.map((c) => `<li><button class="link" data-merge-pick="${esc(c.id)}"><b>${esc(c.label)}</b>${c.hint ? ` <small class="muted">${esc(c.hint)}</small>` : ""}</button></li>`).join("")}</ul>`
          : `<p class="muted">${state.found ? "Nichts gefunden." : noun.search ? "Tippen, um zu suchen." : "Keine andere Adresse bei dieser Firma."}</p>`
      }</div>`;
    return;
  }
  const what =
    kind === "company" && preview
      ? `<ul class="rows">${moveLine(preview.moves.deals, "Deal", "Deals")}${moveLine(preview.moves.contacts, "Person", "Personen")}${moveLine(preview.moves.mails, "Mail", "Mails")}${moveLine(preview.moves.inquiries, "Anfrage", "Anfragen")}${moveLine(preview.moves.activities, "Notiz, Anruf oder Termin", "Notizen, Anrufe und Termine")}${moveLine(preview.moves.addresses, "Adresse", "Adressen")}${moveLine(preview.moves.emails, "E-Mail für Rechnung oder Bestellung", "E-Mails für Rechnung und Bestellung")}</ul>
         ${preview.moves.emailsDuplicate ? `<p class="muted">${preview.moves.emailsDuplicate} doppelte ${preview.moves.emailsDuplicate === 1 ? "E-Mail entfällt" : "E-Mails entfallen"}, „${esc(target.label)}“ hat sie schon.</p>` : ""}
         <p>Leere Stammdaten von „${esc(target.label)}“ werden aus „${esc(source!.label)}“ ergänzt, die Domains zusammengelegt.</p>`
      : kind === "company"
        ? `<p class="muted">Lade Vorschau …</p>`
        : kind === "contact"
          ? `<p>Die Deals von „${esc(source!.label)}“ wechseln zu „${esc(target.label)}“, leere Felder von „${esc(target.label)}“ werden ergänzt.</p>`
          : `<p>Leere Felder von „${esc(target.label)}“ werden aus „${esc(source!.label)}“ ergänzt.</p>`;
  d.innerHTML = `${head}<div class="merge-b">
      <p class="merge-to"><b>${esc(source!.label)}</b> geht auf in <b>${esc(target.label)}</b>${target.hint ? ` <small class="muted">${esc(target.hint)}</small>` : ""}</p>
      ${what}
      <p class="muted">Die alte ID bleibt als Verweis auf „${esc(target.label)}“; Apps, die sie gespeichert haben, erfahren die neue. Das lässt sich nicht rückgängig machen.</p>
      <div class="fbtn"><button class="btn primary" data-merge-go ${state.busy || (kind === "company" && !preview) ? "disabled" : ""}>Zusammenführen</button>
        ${kind === "company" && preview ? `<button class="btn" data-merge-swap>Andersherum</button>` : ""}<button class="btn" data-merge-back>Zurück</button></div></div>`;
}

function onInput(e: Event): void {
  const t = e.target as HTMLInputElement;
  if (t.name !== "q") return;
  state.q = t.value;
  clearTimeout(timer);
  timer = setTimeout(() => void search(), 250);
}

async function search(): Promise<void> {
  const q = state.q.trim();
  if (!q) state.found = null;
  else if (state.kind === "company") {
    state.found = (await S.api.companies(q, true).catch(() => [])).map((c) => ({ id: c.id, version: null, label: c.name, hint: [c.domains[0], c.city].filter(Boolean).join(" · ") }));
  } else {
    // Only persons with a company: the version is read from the company page.
    state.found = (await S.api.search(q).catch(() => null))?.contacts
      .filter((k) => k.companyId)
      .map((k) => ({ id: k.id, version: null, label: k.name ?? k.email ?? "Ohne Namen", hint: [k.email, k.company].filter(Boolean).join(" · "), companyId: k.companyId })) ?? [];
  }
  const d = dialog();
  // Typed on meanwhile, picked one, or closed: this answer is no longer wanted.
  if (state.q.trim() !== q || state.target || !d.open) return;
  const caret = d.querySelector<HTMLInputElement>("input[name=q]")?.selectionStart ?? null;
  paint(d);
  const input = d.querySelector<HTMLInputElement>("input[name=q]");
  input?.focus();
  if (input && caret !== null) input.setSelectionRange(caret, caret);
}

async function pick(d: HTMLDialogElement, id: string): Promise<void> {
  state.target = [...state.offered, ...(state.found ?? [])].find((c) => c.id === id) ?? null;
  state.preview = null;
  paint(d);
  if (state.kind !== "company" || !state.target) return;
  try {
    state.preview = await S.api.mergePreview(state.source!.id, state.target.id);
  } catch (err) {
    toast(err instanceof ApiError ? err.message : "Die Vorschau konnte nicht geladen werden.");
    state.target = null;
  }
  paint(d);
}

/** The version of a person found by the search, from its company's page. */
async function contactVersion(c: Choice): Promise<number | null> {
  if (c.version !== null) return c.version;
  if (!c.companyId) return null;
  return (await S.api.company(c.companyId)).contacts.find((k) => k.id === c.id)?.version ?? null;
}

async function go(d: HTMLDialogElement): Promise<void> {
  const { kind, source, target, preview } = state;
  if (!source || !target || state.busy) return;
  state.busy = true;
  paint(d);
  try {
    if (kind === "company") {
      const { id } = await S.api.mergeCompany(source.id, { into: target.id, version: preview!.source.version, intoVersion: preview!.target.version });
      d.close();
      toast(`In ${target.label} zusammengeführt`);
      S.companies = null;
      S.duplicates = null;
      if (location.hash === `#firma-${id}`) await loadCompany();
      else location.hash = `#firma-${id}`;
    } else {
      const intoVersion = kind === "contact" ? await contactVersion(target) : target.version;
      if (intoVersion === null) throw new ApiError(404, "not_found", "Diesen Kontakt gibt es nicht mehr. Bitte neu laden.");
      const body = { into: target.id, version: source.version!, intoVersion };
      if (kind === "contact") await S.api.mergeContact(source.id, body);
      else await S.api.mergeAddress(state.companyId, source.id, body);
      d.close();
      toast(`In ${target.label} zusammengeführt`);
      await loadCompany().catch(() => undefined);
    }
  } catch (err) {
    toast(err instanceof ApiError ? err.message : "Das Zusammenführen ist fehlgeschlagen. Bitte erneut versuchen.");
    // Someone changed one of them meanwhile: start again from the reloaded page.
    if (err instanceof ApiError && err.status === 409) {
      d.close();
      await loadCompany().catch(() => undefined);
    }
  } finally {
    state.busy = false;
    if (d.open) paint(d);
  }
  render();
}

async function onClick(e: Event, d: HTMLDialogElement): Promise<void> {
  const t = (e.target as HTMLElement).closest<HTMLElement>("[data-merge-close], [data-merge-pick], [data-merge-back], [data-merge-swap], [data-merge-go]");
  if (e.target === d || t?.dataset.mergeClose !== undefined) return d.close();
  if (!t) return;
  if (t.dataset.mergePick) return pick(d, t.dataset.mergePick);
  if (t.dataset.mergeBack !== undefined) {
    state.target = null;
    state.preview = null;
    return paint(d);
  }
  if (t.dataset.mergeSwap !== undefined && state.preview) {
    // The other way round: the target becomes the duplicate.
    const p = state.preview;
    const was = { id: p.source.id, version: p.source.version, label: p.source.name, hint: p.source.domains.join(", ") };
    Object.assign(state, { source: { id: p.target.id, version: p.target.version, label: p.target.name, hint: p.target.domains.join(", ") }, offered: [was], found: null });
    return pick(d, was.id);
  }
  if (t.dataset.mergeGo !== undefined) return go(d);
}

/** The merge buttons on the company page and the duplicates on the company list; only shown to admins. */
export function bindMerge(): void {
  document.addEventListener("click", (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>("#v-firma [data-merge-company], #v-firma [data-merge-contact], #v-firma [data-merge-address], #v-firmen [data-dup]");
    if (!t || !S.me?.isAdmin) return;
    if (t.dataset.dup !== undefined) {
      const g = S.duplicates?.[Number(t.dataset.dup)];
      if (!g) return;
      // The one with the fewest deals goes into the others; the dialog can turn it round.
      const [rest, last] = [g.companies.slice(0, -1), g.companies.at(-1)!];
      const choice = (c: (typeof g.companies)[number]): Choice => ({ id: c.id, version: null, label: c.name, hint: [c.domains[0], c.city].filter(Boolean).join(" · ") });
      return open("company", last.id, choice(last), rest.map(choice));
    }
    const d = S.company;
    if (!d) return;
    if (t.dataset.mergeCompany !== undefined) {
      return open("company", d.company.id, { id: d.company.id, version: d.company.version, label: d.company.name, hint: d.company.domains.join(", ") }, []);
    }
    if (t.dataset.mergeContact) {
      const label = (k: (typeof d.contacts)[number]) => k.name ?? k.email ?? "Ohne Namen";
      const choice = (k: (typeof d.contacts)[number]): Choice => ({ id: k.id, version: k.version, label: label(k), hint: [k.email, k.role].filter(Boolean).join(" · "), companyId: d.company.id });
      const k = d.contacts.find((x) => x.id === t.dataset.mergeContact);
      if (k) open("contact", d.company.id, choice(k), d.contacts.map(choice));
      return;
    }
    if (t.dataset.mergeAddress) {
      const choice = (a: (typeof d.addresses)[number]): Choice => ({ id: a.id, version: a.version, label: [ADDRESS_KIND[a.kind], a.label].filter(Boolean).join(" "), hint: addressLine(a) });
      const a = d.addresses.find((x) => x.id === t.dataset.mergeAddress);
      if (a) open("address", d.company.id, choice(a), d.addresses.map(choice));
    }
  });
}
